import axios from 'axios'
import router from '@/router'

// set preview state
const url = new URL(location.href)
const previewToken = url.searchParams.get('preview')

if (previewToken) {
  window.TIMES_PREVIEW = previewToken
} else if (process.env.VUE_APP_DEV) {
  window.TIMES_PREVIEW = true
}

let preview = window.TIMES_PREVIEW // csrf or true

let baseURL = process.env.VUE_APP_API_URL
let rootURL = process.env.VUE_APP_ROOT_URL

const api = axios.create({
  baseURL,
  withCredentials: !!preview
})

// include csrf, to view private API
api.interceptors.request.use(async (config) => {
  if (preview) {
    config.headers['X-CSRF'] = preview
  }
  return config
})

// auth error? redirect to login
api.interceptors.response.use(res => res, (err) => {
  const res = err.response
  if (res && (res.status === 401 || res.status === 403)) {
    router.push('/en/login')
    // window.location.href = process.env.VUE_APP_PANEL
  }
  Promise.reject(err)
})

export default {
  session () {
    // get a CSRF token if in DEV mode
    // otherwise provided by the ?preview= param
    return api.get('csrf').then(({ data }) => {
      preview = data && data.csrf
      window.TIMES_PREVIEW = preview
    })
  },

  // locate via cloudflare
  locate ({ state, commit }) {
    if (state.location) return

    return fetch('https://www.cloudflare.com/cdn-cgi/trace').then(res => {
      return res.text().then(data => {
        // const re = /loc=(\w*)$/gm
        // const location = [...data.matchAll(re)][0][1]
        const location = 'CN'
        commit('LOCATION', 'CN')

        baseURL =
          preview
            ? location !== 'CN'
              // Is preview in China : Outside of China
              ? process.env.VUE_APP_MIRROR_API_URL_PREVIEW : process.env.VUE_APP_API_URL_PREVIEW
            : location !== 'CN'
              // Live view China : Outside of China
              ? process.env.VUE_APP_MIRROR_API_URL : process.env.VUE_APP_API_URL

        rootURL =
          preview
            ? location !== 'CN'
              // Is preview in China : Outside of China
              ? process.env.VUE_APP_MIRROR_ROOT_URL_PREVIEW : process.env.VUE_APP_ROOT_URL_PREVIEW
            : location !== 'CN'
              // Live view China : Outside of China
              ? process.env.VUE_APP_MIRROR_ROOT_URL : process.env.VUE_APP_ROOT_URL

        // update api baseURL
        api.defaults.baseURL = baseURL
      })
    })
  },

  getProgram ({ state, commit }, section = 'program') {
    const sections = {
      program: 'program',
      learning: 'learning-and-experience',
      journals: 'journal'
    }
    if (state[section]) return Promise.resolve()

    return api.get(sections[section]).then(({ data }) => {
      commit(section, data)
    })
  },
  getItem ({ commit }, { params }) {
    const { pathMatch, type, slug } = params
    const url = `${pathMatch}/${type ? type + '/' : ''}${slug}`
    return api.get(url).then(({ data }) => {
      commit('SINGLE', data)
    })
  },
  async getWeather ({ commit }) {
    try {
      const response = await api.get('weather')
      commit('CLOSED', response.data.closed)
      commit('WEATHER', response.data.list)
      return true
    } catch (error) {
      // Error 😨
      if (error.response) {
        /*
         * The request was made and the server responded with a
         * status code that falls out of the range of 2xx
         */
        console.log(error.response.data)
        console.log(error.response.status)
        console.log(error.response.headers)
      } else if (error.request) {
        /*
         * The request was made but no response was received, `error.request`
         * is an instance of XMLHttpRequest in the browser and an instance
         * of http.ClientRequest in Node.js
         */
        console.log(error.request)
      } else {
        // Something happened in setting up the request and triggered an Error
        console.log('Error', error.message)
      }
      console.log(error)
    }
  },
  getBooks ({ commit }) {
    return api.get('books').then(({ data }) => {
      commit('BOOKS', data)
    })
  },
  getBook ({ commit }, slug) {
    return api.get(`books/${slug}`).then(({ data }) => {
      commit('BOOK', data)
    })
  },
  getAbout ({ state, commit }) {
    if (state.about) return
    return api.get('about').then(({ data }) => {
      commit('ABOUT', data)
      commit('CLOSED', data.cn.closed)
    })
  },
  getArticle ({ commit }, slug) {
    return api.get(slug).then(({ data }) => {
      commit('ARTICLE', data)
    })
  },
  getFeatured ({ state, commit }) {
    if (state.featured) return
    return api.get('featured').then(({ data }) => {
      commit('FEATURED', data)
    })
  },
  getCurrent ({ commit }) {
    return api.get('current').then(({ data }) => {
      commit('CURRENT', data)
    })
  },
  getNews ({ commit }) {
    return api.get('news').then(({ data }) => {
      commit('NEWS', data)
    })
  },
  getNewsItem ({ commit }, slug) {
    return api.get(`news/${slug}`).then(({ data }) => {
      commit('NEWS_ITEM', data)
    })
  },
  visitPage ({ commit }) {
    return api.get('pages/visit').then(({ data }) => {
      commit('VISIT_PAGE', data)
    })
  },
  getPage ({ state, commit }, slug) {
    if (state.pages[slug]) return
    return api.get(`pages/${slug}`).then(({ data }) => {
      commit('CONTENT_PAGE', { slug, data })
    })
  },
  getEntity ({ state, commit }, id) {
    if (state.entities[id]) return

    return api.get(`entity/${id}`).then(({ data }) => {
      commit('ENTITY', { id, data })
    })
  },
  search ({ commit, state }) {
    commit('SET_SEARCH_RESULTS', null)

    return axios.get(`${rootURL}${state.locale}/search.json`, {
      params: { q: state.searchTerm }
    }).then(({ data }) => {
      commit('SET_SEARCH_RESULTS', data)
    })
  },
  setTimes ({ commit }) {
    const cnTime = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Shanghai' }))
    const deTime = new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/Berlin' }))
    const open = cnTime.getDay() !== 1 && (cnTime.getHours() >= 10 && cnTime.getHours() <= 18)

    const timeData = {
      cn: {
        time: cnTime,
        night: cnTime.getHours() > 20 || cnTime.getHours() < 6,
        open: open
      },
      de: {
        time: deTime,
        night: deTime.getHours() > 20 || deTime.getHours() < 6
      }
    }

    commit('TIME', timeData)
  }
}
